import * as React from 'react';
// @ts-expect-error
import ReactTooltip from 'react-tooltip';

import classnames from 'classnames';
import moment from 'moment';

import {Button} from 'shells/button/button';
// @ts-expect-error - imported from .js/.jsx file
import {LoadingIcon} from 'shells/icon/svg/loading-icon';
import {RelativeTimestamp} from 'shells/timestamp/relative-timestamp';

import {Chevron, loadingIconColor} from '../../../sales-automation/chevron';
import type {Variant as ChevronVariant} from '../../../sales-automation/chevron';

import styles from './lead-stage-chevron.module.css';

type Props = {
    stageId: string;
    stageName: string;
    isLoading: boolean;
    isFirstChild: boolean;
    isLastChild: boolean;
    isActive: boolean;
    isPast: boolean;
    isHot: boolean;
    isSelected: boolean;
    status: string;
    onActivateStage: (stageId: string) => any;
    onSelectStage: (stage: {id: string}) => any;
    timeInStage?: string;
    stageOverdueTime?: any;
};

export class LeadStageChevron extends React.PureComponent<Props> {
    confirmationAnchor: HTMLElement | null | undefined;

    componentDidMount() {
        ReactTooltip.rebuild();
    }

    render() {
        // Grep aid: Advance to this stage, Move to this stage
        const moveVerb = this.props.isPast ? 'Move' : 'Advance';
        const variant = this.chevronVariant();
        const isPastOrActive = this.props.isPast || this.props.isActive;

        const stageOverdueTime =
            this.props.stageOverdueTime &&
            moment.unix(this.props.stageOverdueTime).isBefore(moment().subtract(1, 'h'))
                ? this.props.stageOverdueTime
                : undefined;

        const classNames = classnames('ui-lead-stage', {
            'ui-lead-stage--selected': this.props.isSelected,
        });

        return (
            <div
                data-stage-id={this.props.stageId}
                ref={(ref) => (this.confirmationAnchor = ref)}
                className={classnames(classNames, styles['stage-container'])}
            >
                {this.props.isSelected && !this.props.isActive ? (
                    <div className={styles['stage-confirm']}>
                        <Button variant='text-primary' onClick={this.handleActivateStage}>
                            {`${moveVerb} to this stage`}
                        </Button>
                    </div>
                ) : undefined}
                <Chevron
                    hasStraightLeft={this.props.isFirstChild}
                    hasStraightRight={this.props.isLastChild}
                    onClick={this.handleSelectStage}
                    variant={variant}
                    isActive={this.props.isActive}
                    isSelected={this.props.isSelected}
                    isPast={this.props.isPast}
                    hasRightMargin={!this.props.isLastChild}
                    // This isn’t my favorite thing, but it’s needed to give centering
                    // margin at the right side of the stage scroll container.
                    style={this.props.isLastChild ? {marginRight: '16px'} : undefined}
                    height='short'
                    width={175}
                >
                    {this.props.isLoading ? (
                        <div className={styles['stage-info']}>
                            <LoadingIcon fill={loadingIconColor(variant, isPastOrActive)} />
                        </div>
                    ) : (
                        <div
                            data-tip={this.getStageTooltipText()}
                            data-place='bottom'
                            className={styles['stage-info']}
                        >
                            {this.props.stageName}
                            {this.props.isActive && stageOverdueTime ? (
                                <div className={styles['secondary-text']}>
                                    <RelativeTimestamp
                                        format='fromNow'
                                        datetime={stageOverdueTime}
                                        hideAgoString={true}
                                    />
                                    &nbsp;overdue
                                </div>
                            ) : undefined}
                        </div>
                    )}
                </Chevron>
            </div>
        );
    }

    handleActivateStage = () => {
        this.props.onActivateStage(this.props.stageId);
    };

    handleSelectStage = () => {
        this.props.onSelectStage({id: this.props.stageId});
    };

    getStageTooltipText = (): string | null | undefined => {
        if (!this.props.timeInStage) {
            // If we don't have a time for this stage, we show no tooltip
            return null;
        }

        let text = this.props.isActive
            ? `In this stage for ${this.props.timeInStage}`
            : `Spent ${this.props.timeInStage} in this stage.`;

        if (
            this.props.stageOverdueTime &&
            // If its not already overdue...
            !moment.unix(this.props.stageOverdueTime).isBefore(moment())
        ) {
            const datetimeValue = moment.unix(this.props.stageOverdueTime);

            const timeTillOverdue = datetimeValue.isValid() ? datetimeValue.fromNow(true) : null;

            if (timeTillOverdue) {
                text = text.concat(`<br />It will be overdue in ${timeTillOverdue}.`);
            }
        }

        return text;
    };

    chevronVariant(): ChevronVariant {
        const {status} = this.props;

        if (status === 'WON') {
            return 'green';
        }

        if (status === 'LOST' || status === 'PENDING' || status === 'CANCELLED') {
            return 'grey';
        }

        if (this.props.isHot) {
            return 'orange';
        }

        return 'blue';
    }
}
